import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Bike, MapPin, Clock, Phone, User, AlertCircle, DollarSign } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import BikerLayout from '../../components/biker/BikerLayout';

const BikerRides = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    fetchAvailableRides();
  }, []);

  const fetchAvailableRides = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/bikers/available-deliveries');
      
      // Filter only regular rides (not deliveries)
      const ridesOnly = response.data.rides.filter(ride => ride.type !== 'delivery');
      setRides(ridesOnly);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rides:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load rides',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleAcceptRide = async (rideId) => {
    setAccepting(rideId);
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/bikers/delivery-action', {
        rideId: rideId,
        action: 'accept'
      });

      toast({
        title: 'Ride Accepted!',
        description: 'Navigate to Active Ride to start',
      });

      // Redirect to active ride page
      navigate('/biker/active-ride');
    } catch (error) {
      console.error('Error accepting ride:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to accept ride',
        variant: 'destructive',
      });
      setAccepting(null);
    }
  };

  if (loading) {
    return (
      <BikerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </BikerLayout>
    );
  }

  return (
    <BikerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Available Rides</h1>
            <p className="text-gray-600 mt-1">Motorbike ride requests near you</p>
          </div>
          <Button onClick={fetchAvailableRides} variant="outline">
            Refresh
          </Button>
        </div>

        {rides.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Bike className="h-8 w-8 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Rides Available</h2>
            <p className="text-gray-600 mb-4">
              There are no ride requests at the moment. Make sure you're online to receive requests.
            </p>
            <Button onClick={fetchAvailableRides} variant="outline">
              Refresh List
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {rides.map((ride) => (
              <Card key={ride.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <Bike className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">Motorbike Ride</h3>
                      <p className="text-sm text-gray-600">
                        {ride.rideTypeId || 'Standard Ride'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600">K{ride.price}</p>
                    <p className="text-sm text-gray-600">{ride.distance?.toFixed(1)} km</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Pickup</p>
                      <p className="text-sm text-gray-900">{ride.pickup}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Dropoff</p>
                      <p className="text-sm text-gray-900">{ride.dropoff}</p>
                    </div>
                  </div>

                  {ride.userName && (
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-gray-400" />
                      <p className="text-sm text-gray-900">{ride.userName}</p>
                      {ride.userPhone && (
                        <>
                          <span className="text-gray-400">•</span>
                          <Phone className="h-4 w-4 text-gray-400" />
                          <p className="text-sm text-gray-900">{ride.userPhone}</p>
                        </>
                      )}
                    </div>
                  )}

                  {ride.stops && ride.stops.length > 0 && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-orange-600" />
                      <p className="text-sm text-orange-700 font-medium">
                        {ride.stops.length} stop{ride.stops.length > 1 ? 's' : ''} included
                      </p>
                    </div>
                  )}

                  {ride.createdAt && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        Posted {new Date(ride.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  )}
                </div>

                {ride.paymentMethod && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-gray-50 rounded">
                    <DollarSign className="h-4 w-4 text-gray-600" />
                    <p className="text-sm text-gray-700">
                      Payment: {ride.paymentMethod === 'cash' ? 'Cash' : 'Card'}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => handleAcceptRide(ride.id)}
                  disabled={accepting === ride.id}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  {accepting === ride.id ? 'Accepting...' : 'Accept Ride'}
                </Button>
              </Card>
            ))}
          </div>
        )}

        <Card className="p-4 bg-orange-50 border-orange-200">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-900">Riding Tips</p>
              <p className="text-sm text-orange-700 mt-1">
                You can accept one ride at a time. Always wear your helmet and follow traffic rules for safety.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </BikerLayout>
  );
};

export default BikerRides;
